import { Controller } from "@hotwired/stimulus"

// Handles search, filter, sort, view mode toggle, region/date filters,
// and dynamic tab switching for the construction project list page.
export default class extends Controller {
  static targets = [
    "searchInput", "frame", "filterBtn", "viewBtn", "viewModeGroup", "tabBtn",
    "regionCheckbox", "regionToggleAll", "regionBadge",
    "dateFrom", "dateTo", "datePresets",
    "filterBadge", "exportButton",
    "actionItemsExportModal", "actionItemsExportKindList",
    "actionItemsExportStatus", "actionItemsExportScope",
    "actionItemsExportPeoplePicker", "actionItemsExportSelectedChips",
    "actionItemsExportSearchInput", "actionItemsExportSearchResults"
  ]
  static values = {
    viewMode: { type: String, default: "thin" },
    currentView: { type: String, default: "user" },
    savedFilters: { type: Array, default: [] },
    savedSort: { type: String, default: "name" },
    savedRegions: { type: Array, default: [] },
    savedDateFrom: { type: String, default: "" },
    savedDateTo: { type: String, default: "" },
    savedDatePreset: { type: String, default: "" },
    actionItemsExportCsvUrl: { type: String, default: "" },
    actionItemsExportPdfUrl: { type: String, default: "" },
    actionItemsExportMembersUrl: { type: String, default: "" },
    actionItemsExportCurrentEmail: { type: String, default: "" },
    actionItemsExportCurrentName: { type: String, default: "" },
    actionItemsExportNoMatchesLabel: { type: String, default: "No matches" },
    actionItemsExportLoadingLabel: { type: String, default: "Loading…" }
  }

  connect() {
    this._debounceTimer = null
    // Empty array means "all"
    this._currentFilters = this.savedFiltersValue.length ? [...this.savedFiltersValue] : []
    this._currentSort = this.savedSortValue || "name"
    this._currentDatePreset = this.savedDatePresetValue || ""

    // Restore saved date range
    if (this._currentDatePreset) {
      this._applyPresetDates(this._currentDatePreset)
    } else if (this.savedDateFromValue || this.savedDateToValue) {
      if (this.savedDateFromValue && this.hasDateFromTarget) this.dateFromTarget.value = this.savedDateFromValue
      if (this.savedDateToValue && this.hasDateToTarget) this.dateToTarget.value = this.savedDateToValue
    }

    this._updateFilterBadges()
    this._updateRegionBadge()
    this._updateDatePresetChips()
    this._updateFilterBadgeCount()
  }

  // Debounced search
  search() {
    clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => this._reload(), 300)
  }

  // Status filter from badge clicks (multi-select toggle)
  filter(event) {
    const value = event.currentTarget.dataset.filter
    const idx = this._currentFilters.indexOf(value)
    if (idx >= 0) {
      this._currentFilters.splice(idx, 1)
    } else {
      this._currentFilters.push(value)
    }

    this._updateFilterBadges()
    this._reload()
  }

  _updateFilterBadges() {
    const badgeClasses = [
      "badge-neutral", "badge-status-active", "badge-status-on_hold",
      "badge-status-turn_over_phase", "badge-status-completed"
    ]

    this.filterBtnTargets.forEach((b) => {
      const ownClass = b.dataset.badgeClass
      badgeClasses.forEach((c) => b.classList.remove(c))

      const inactiveClasses = ["bg-white", "text-text-muted", "border-gray-300",
        "hover:text-gray-700", "hover:border-gray-400"]

      if (this._currentFilters.includes(b.dataset.filter)) {
        b.classList.add(ownClass, "border-transparent")
        b.classList.remove(...inactiveClasses)
      } else {
        b.classList.add(...inactiveClasses)
        b.classList.remove("border-transparent")
      }
    })
  }

  // Sort from dropdown
  sort(event) {
    this._currentSort = event.currentTarget.value
    this._reload()
  }

  // Three-mode view toggle (card / thin / tall)
  toggleView(event) {
    const mode = event.currentTarget.dataset.viewMode
    this.viewModeValue = mode

    this.viewBtnTargets.forEach((b) => {
      if (b.dataset.viewMode === mode) {
        b.classList.add("text-brand", "font-bold")
        b.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        b.classList.remove("text-brand", "font-bold")
        b.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    // Save preference
    fetch(`/company/save_view_mode?mode=${mode}`, {
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
    })

    this._reload()
  }

  // Dynamic tab switching (user / tag / portfolio)
  switchView(event) {
    const view = event.currentTarget.dataset.view
    this.currentViewValue = view

    // Update tab button active styling
    this.tabBtnTargets.forEach((b) => {
      if (b.dataset.view === view) {
        b.classList.add("text-brand", "font-bold")
        b.classList.remove("text-text-muted", "font-medium", "hover:text-gray-700")
      } else {
        b.classList.remove("text-brand", "font-bold")
        b.classList.add("text-text-muted", "font-medium", "hover:text-gray-700")
      }
    })

    // Save preference server-side
    fetch(`/company/save_view_mode?view=${view}`, {
      headers: { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
    })

    this._reload()
  }

  // Region filter: individual checkbox change
  filterRegions() {
    const checkboxes = this.regionCheckboxTargets
    const allChecked = checkboxes.every((cb) => cb.checked)
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = allChecked
      toggle.indeterminate = !allChecked && checkboxes.some((cb) => cb.checked)
    })

    this._reload()
  }

  // Region filter: toggle all checkboxes
  toggleAllRegions(event) {
    const checked = event.currentTarget.checked
    this.regionCheckboxTargets.forEach((cb) => { cb.checked = checked })
    this.regionToggleAllTargets.forEach((toggle) => {
      toggle.checked = checked
      toggle.indeterminate = false
    })
    this._reload()
  }

  // Date preset selection (Last 30 days, Last 90 days, etc.)
  selectDatePreset(event) {
    const preset = event.currentTarget.dataset.preset
    this._currentDatePreset = preset

    this._applyPresetDates(preset)
    this._updateDatePresetChips()
    this._reload()
  }

  // Custom date range filter
  filterDates() {
    this._currentDatePreset = ""
    this._updateDatePresetChips()
    this._reload()
  }

  _applyPresetDates(preset) {
    const today = new Date()
    let from = ""
    let to = ""

    if (preset === "30" || preset === "90" || preset === "365") {
      const days = parseInt(preset)
      const fromDate = new Date(today)
      fromDate.setDate(fromDate.getDate() - days)
      from = this._formatDate(fromDate)
      to = this._formatDate(today)
    } else if (preset === "ytd") {
      from = `${today.getFullYear()}-01-01`
      to = this._formatDate(today)
    }

    if (this.hasDateFromTarget) this.dateFromTarget.value = from
    if (this.hasDateToTarget) this.dateToTarget.value = to
  }

  _updateDatePresetChips() {
    if (!this.hasDatePresetsTarget) return
    const buttons = this.datePresetsTarget.querySelectorAll("button")
    const activeClasses = ["bg-brand/10", "text-brand", "font-medium", "border-brand/30"]
    const inactiveClasses = ["text-gray-500", "hover:bg-gray-50", "border-gray-200"]

    buttons.forEach((btn) => {
      const isActive = btn.dataset.preset === (this._currentDatePreset || "")
      btn.classList.remove(...activeClasses, ...inactiveClasses)
      if (isActive) {
        btn.classList.add(...activeClasses)
      } else {
        btn.classList.add(...inactiveClasses)
      }
    })
  }

  // Clear all filters
  clearFilters() {
    // Reset status filters to all
    this._currentFilters = []
    this._updateFilterBadges()

    // Reset regions to all
    if (this.hasRegionCheckboxTarget) {
      this.regionCheckboxTargets.forEach((cb) => { cb.checked = true })
    }
    if (this.hasRegionToggleAllTarget) {
      this.regionToggleAllTargets.forEach((t) => { t.checked = true; t.indeterminate = false })
    }

    // Reset date to all
    this._currentDatePreset = ""
    if (this.hasDateFromTarget) this.dateFromTarget.value = ""
    if (this.hasDateToTarget) this.dateToTarget.value = ""
    this._updateDatePresetChips()

    // Reset sort to default
    this._currentSort = "name"
    const sortSelect = this.element.querySelector(".sort-select")
    if (sortSelect) sortSelect.value = "name"

    this._reload()
  }

  _formatDate(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${m}-${d}`
  }

  exportManagementReport() {
    // Close dropdown
    const dropdown = this.element.querySelector('[data-controller="dropdown"] [data-dropdown-target="menu"]')
    if (dropdown) dropdown.classList.add("hidden")

    this._showExportSpinner()

    // Build params from current filter state
    const params = new URLSearchParams()

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) params.set("q", q)

    if (this._currentFilters.length > 0) {
      this._currentFilters.forEach((f) => params.append("filter[]", f))
    }
    params.set("sort", this._currentSort || "name")

    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length
      if (selected.length < total) {
        selected.forEach((r) => params.append("regions[]", r))
      }
    }

    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      params.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      params.set("date_to", this.dateToTarget.value)
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    fetch(`/company/export_management_report`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: params.toString()
    })
      .then((response) => {
        if (!response.ok) throw new Error("Export failed")
        return response.blob()
      })
      .then((blob) => {
        const url = URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        const today = new Date().toISOString().split("T")[0]
        a.download = `management_report_${today}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        this._showToast("Management report exported successfully")
      })
      .catch(() => this._showToast("Failed to export. Please try again.", "error"))
      .finally(() => this._hideExportSpinner())
  }

  // ── Cross-project Action Items export ──────────────────────────

  openActionItemsExportModal() {
    if (!this.hasActionItemsExportModalTarget) return
    // Closes any open dropdowns (the dropdown controller listens for modal:open)
    document.dispatchEvent(new CustomEvent("modal:open"))
    this.actionItemsExportModalTarget.classList.remove("hidden")
    this._ensureActionItemsMembersLoaded()
    if (!this._actionItemsSelectedEmails) {
      this._actionItemsSelectedEmails = new Map()
    }
    if (!this._actionItemsOutsideClickBound) {
      this._actionItemsOutsideClickHandler = (e) => {
        if (!this.hasActionItemsExportPeoplePickerTarget) return
        if (!this.actionItemsExportPeoplePickerTarget.contains(e.target)) {
          this._closeActionItemsPeopleDropdown()
        }
      }
      document.addEventListener("click", this._actionItemsOutsideClickHandler)
      this._actionItemsOutsideClickBound = true
    }
  }

  closeActionItemsExportModal() {
    if (this.hasActionItemsExportModalTarget) {
      this.actionItemsExportModalTarget.classList.add("hidden")
    }
    this._closeActionItemsPeopleDropdown()
    if (this._actionItemsOutsideClickBound) {
      document.removeEventListener("click", this._actionItemsOutsideClickHandler)
      this._actionItemsOutsideClickBound = false
    }
  }

  disconnect() {
    if (this._actionItemsOutsideClickBound) {
      document.removeEventListener("click", this._actionItemsOutsideClickHandler)
      this._actionItemsOutsideClickBound = false
    }
  }

  // ── Action Items export: Responsibility "Specific people" picker ──

  onActionItemsScopeChange(event) {
    const scope = event?.target?.value
    if (!this.hasActionItemsExportPeoplePickerTarget) return

    if (scope === "specific") {
      this.actionItemsExportPeoplePickerTarget.classList.remove("hidden")
      // Default to the current user on first activation
      if (!this._actionItemsSelectedEmails || this._actionItemsSelectedEmails.size === 0) {
        if (!this._actionItemsSelectedEmails) this._actionItemsSelectedEmails = new Map()
        const email = this.actionItemsExportCurrentEmailValue
        const name = this.actionItemsExportCurrentNameValue || email
        if (email) {
          this._actionItemsSelectedEmails.set(email, name)
          this._renderActionItemsSelectedChips()
        }
      }
    } else {
      this.actionItemsExportPeoplePickerTarget.classList.add("hidden")
      this._closeActionItemsPeopleDropdown()
    }
  }

  _ensureActionItemsMembersLoaded() {
    if (this._actionItemsMembers || this._actionItemsMembersLoading) return
    const url = this.actionItemsExportMembersUrlValue
    if (!url) return
    this._actionItemsMembersLoading = true
    fetch(url, { headers: { Accept: "application/json" } })
      .then((r) => r.json())
      .then((data) => {
        this._actionItemsMembers = Array.isArray(data) ? data : []
      })
      .catch(() => { this._actionItemsMembers = [] })
      .finally(() => { this._actionItemsMembersLoading = false })
  }

  openActionItemsPeopleDropdown() {
    this._ensureActionItemsMembersLoaded()
    this._renderActionItemsSearchResults(this.actionItemsExportSearchInputTarget.value || "")
  }

  _closeActionItemsPeopleDropdown() {
    if (this.hasActionItemsExportSearchResultsTarget) {
      this.actionItemsExportSearchResultsTarget.classList.add("hidden")
      this.actionItemsExportSearchResultsTarget.innerHTML = ""
    }
  }

  onActionItemsPeopleSearch(event) {
    this._renderActionItemsSearchResults(event.target.value || "")
  }

  _renderActionItemsSearchResults(query) {
    if (!this.hasActionItemsExportSearchResultsTarget) return
    const list = this.actionItemsExportSearchResultsTarget
    const members = this._actionItemsMembers || []
    const q = query.trim().toLowerCase()
    const selected = this._actionItemsSelectedEmails || new Map()

    const matches = members
      .filter((u) => !selected.has(u.email))
      .filter((u) => {
        if (!q) return true
        return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)
      })
      .slice(0, 30)

    if (matches.length === 0) {
      const label = this._actionItemsMembersLoading ? this.actionItemsExportLoadingLabelValue : this.actionItemsExportNoMatchesLabelValue
      list.innerHTML = `<div class="px-3 py-2 text-[13px] text-gray-500">${this._escapeHtml(label)}</div>`
    } else {
      list.innerHTML = matches.map((u) => `
        <button type="button"
                data-email="${this._escapeAttr(u.email)}"
                data-name="${this._escapeAttr(u.name || u.email)}"
                class="ai-export-user-row w-full text-left px-3 py-1.5 text-[13px] text-gray-700 hover:bg-gray-50 cursor-pointer flex flex-col">
          <span class="font-medium">${this._escapeHtml(u.name || u.email)}</span>
          ${u.name ? `<span class="text-gray-500 text-[12px]">${this._escapeHtml(u.email)}</span>` : ""}
        </button>
      `).join("")
      list.querySelectorAll(".ai-export-user-row").forEach((row) => {
        row.addEventListener("click", (e) => {
          e.stopPropagation()
          const email = row.dataset.email
          const name = row.dataset.name
          this._addActionItemsSelected(email, name)
          this.actionItemsExportSearchInputTarget.value = ""
          this._renderActionItemsSearchResults("")
          this.actionItemsExportSearchInputTarget.focus()
        })
      })
    }
    list.classList.remove("hidden")
  }

  _addActionItemsSelected(email, name) {
    if (!email) return
    if (!this._actionItemsSelectedEmails) this._actionItemsSelectedEmails = new Map()
    this._actionItemsSelectedEmails.set(email, name || email)
    this._renderActionItemsSelectedChips()
  }

  _removeActionItemsSelected(email) {
    if (!this._actionItemsSelectedEmails) return
    this._actionItemsSelectedEmails.delete(email)
    this._renderActionItemsSelectedChips()
  }

  _renderActionItemsSelectedChips() {
    if (!this.hasActionItemsExportSelectedChipsTarget) return
    const container = this.actionItemsExportSelectedChipsTarget
    const selected = this._actionItemsSelectedEmails || new Map()
    if (selected.size === 0) {
      container.innerHTML = ""
      return
    }
    container.innerHTML = Array.from(selected.entries()).map(([email, name]) => `
      <span class="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 bg-gray-100 border border-gray-200 rounded-full text-[13px] text-gray-700">
        ${this._escapeHtml(name || email)}
        <button type="button" data-email="${this._escapeAttr(email)}"
                class="ai-export-chip-remove w-4 h-4 inline-flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer">×</button>
      </span>
    `).join("")
    container.querySelectorAll(".ai-export-chip-remove").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        this._removeActionItemsSelected(btn.dataset.email)
      })
    })
  }

  _escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }

  _escapeAttr(str) {
    return this._escapeHtml(str)
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  _buildActionItemsExportParams() {
    const params = new URLSearchParams()

    if (this.hasActionItemsExportKindListTarget) {
      const boxes = Array.from(this.actionItemsExportKindListTarget.querySelectorAll("input[type=checkbox]"))
      const checked = boxes.filter((cb) => cb.checked)
      // Only send kind_ids for a strict subset; all (or none) selected means "all types"
      if (checked.length > 0 && checked.length < boxes.length) {
        checked.forEach((cb) => params.append("kind_ids[]", cb.value))
      }
    }

    if (this.hasActionItemsExportStatusTarget) {
      const status = this.actionItemsExportStatusTarget.querySelector("input[type=radio]:checked")?.value
      if (status) params.set("status", status)
    }

    if (this.hasActionItemsExportScopeTarget) {
      const scope = this.actionItemsExportScopeTarget.querySelector("input[type=radio]:checked")?.value
      if (scope === "responsible") {
        params.set("scope", "responsible")
      } else if (scope === "specific") {
        const emails = this._actionItemsSelectedEmails ? Array.from(this._actionItemsSelectedEmails.keys()) : []
        if (emails.length > 0) {
          params.set("scope", "specific")
          emails.forEach((e) => params.append("responsible_emails[]", e))
        }
      }
    }

    return params.toString()
  }

  exportActionItemsCsv() {
    const qs = this._buildActionItemsExportParams()
    window.open(this.actionItemsExportCsvUrlValue + (qs ? `?${qs}` : ""), "_blank")
    this.closeActionItemsExportModal()
  }

  exportActionItemsPdf() {
    const qs = this._buildActionItemsExportParams()
    window.open(this.actionItemsExportPdfUrlValue + (qs ? `?${qs}` : ""), "_blank")
    this.closeActionItemsExportModal()
  }

  _showExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    btn._originalHTML = btn.innerHTML
    btn.style.width = `${btn.offsetWidth}px`
    btn.classList.add("justify-center")
    btn.disabled = true
    btn.innerHTML = `<svg class="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>`
  }

  _hideExportSpinner() {
    if (!this.hasExportButtonTarget) return
    const btn = this.exportButtonTarget
    if (btn._originalHTML) {
      btn.innerHTML = btn._originalHTML
      delete btn._originalHTML
    }
    btn.style.width = ""
    btn.classList.remove("justify-center")
    btn.disabled = false
  }

  _reload() {
    this._updateFilterBadges()
    this._updateRegionBadge()
    this._updateFilterBadgeCount()
    const frame = this.frameTarget
    const url   = this._buildUrl()
    frame.src   = url
  }

  _buildUrl() {
    // Determine endpoint based on currentView
    let endpoint
    switch (this.currentViewValue) {
      case "tag":
        endpoint = `/company/tags`
        break
      case "portfolio":
        endpoint = `/company/portfolios_list`
        break
      default:
        endpoint = `/company/projects`
    }

    const url = new URL(endpoint, window.location.origin)

    url.searchParams.set("page", "1")
    url.searchParams.set("format", "html")

    const q = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    if (q) url.searchParams.set("q", q)

    // Status filters (multi-select): empty = all
    if (this._currentFilters.length > 0) {
      this._currentFilters.forEach((f) => url.searchParams.append("filter[]", f))
    }
    url.searchParams.set("sort", this._currentSort || "name")

    url.searchParams.set("view_mode", this.viewModeValue)

    // Region filter params
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).map((cb) => cb.value)
      const total = this.regionCheckboxTargets.length

      // Only send filter when not all are selected
      if (selected.length < total) {
        selected.forEach((r) => url.searchParams.append("regions[]", r))
      }
    }

    // Date filter params
    if (this.hasDateFromTarget && this.dateFromTarget.value) {
      url.searchParams.set("date_from", this.dateFromTarget.value)
    }
    if (this.hasDateToTarget && this.dateToTarget.value) {
      url.searchParams.set("date_to", this.dateToTarget.value)
    }
    if (this._currentDatePreset) {
      url.searchParams.set("date_preset", this._currentDatePreset)
    }

    return url.toString()
  }

  _updateRegionBadge() {
    if (this.hasRegionBadgeTarget && this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).length
      const total = this.regionCheckboxTargets.length
      if (selected < total) {
        this.regionBadgeTarget.textContent = `${selected}/${total}`
        this.regionBadgeTarget.classList.remove("hidden")
      } else {
        this.regionBadgeTarget.classList.add("hidden")
      }
    }
  }

  _updateFilterBadgeCount() {
    if (!this.hasFilterBadgeTarget) return
    let count = 0

    // Status: count if not all-or-none selected
    if (this._currentFilters.length > 0) count++

    // Region: count if not all selected
    if (this.hasRegionCheckboxTarget) {
      const selected = this.regionCheckboxTargets.filter((cb) => cb.checked).length
      const total = this.regionCheckboxTargets.length
      if (selected < total) count++
    }

    // Date: count if a date range is set
    const hasFrom = this.hasDateFromTarget && this.dateFromTarget.value
    const hasTo = this.hasDateToTarget && this.dateToTarget.value
    if (hasFrom || hasTo) count++

    if (count > 0) {
      this.filterBadgeTarget.textContent = count
      this.filterBadgeTarget.classList.remove("hidden")
      this.filterBadgeTarget.classList.add("inline-flex")
    } else {
      this.filterBadgeTarget.classList.add("hidden")
      this.filterBadgeTarget.classList.remove("inline-flex")
    }
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }
}
